import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { statsData, testimonials } from '@/mocks/siteData';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: '关于我们' },
  ];

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '黄游免费下载',
    url: 'https://huangyou-free-download.com',
    description: '海量黄游免费下载资源，涵盖各类热门黄游，安全快速，持续更新',
    foundingDate: '2020',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'support@huangyou-free-download.com',
      availableLanguage: ['Chinese'],
    },
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 黄游免费下载',
    description: '了解黄游免费下载的品牌故事、发展历程和核心优势',
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      return;
    }
    try {
      const response = await fetch('https://readdy.ai/api/form/d9qu42snlsngrm4lm64g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || '';
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else if (!response.ok || serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络连接异常，请稍后重试');
    }
  };

  const teamMembers = [
    { name: '游戏猎人', role: '创始人 & 主编', icon: 'ri-user-star-line', desc: '资深黄游玩家，10年游戏评测经验，致力于为玩家提供最优质的资源推荐。' },
    { name: '手游达人', role: '移动端负责人', icon: 'ri-smartphone-line', desc: '专注手机黄游领域，精通Android/iOS平台，持续发掘优质手机资源。' },
    { name: '安全卫士', role: '安全审核专家', icon: 'ri-shield-check-line', desc: '负责所有资源的严格安全审核，确保每一款游戏的下载安全可靠。' },
    { name: '剧情控', role: '内容创作者', icon: 'ri-quill-pen-line', desc: '视觉小说爱好者，擅长深度游戏评测和剧情分析，撰写高质量攻略文章。' },
  ];

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            {/* Hero */}
            <div className="text-center py-12 md:py-16">
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 mb-5">
                关于我们
              </h1>
              <p className="text-foreground-500 text-base md:text-lg max-w-3xl mx-auto leading-relaxed">
                黄游免费下载是国内领先的黄游资源分享平台，自2020年成立以来，始终坚持以玩家需求为核心，
                持续为广大黄游爱好者提供安全、全面、及时的黄游免费下载服务。
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-20">
              {statsData.map((stat, index) => (
                <div key={index} className="p-6 rounded-xl bg-background-50 border border-background-200/70 text-center">
                  <div className="text-3xl md:text-4xl font-heading font-bold text-primary-500 mb-2">{stat.value}</div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Brand Story */}
            <section className="mb-20">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
                    品牌故事
                  </h2>
                  <div className="space-y-4 text-foreground-600 leading-relaxed text-sm md:text-base">
                    <p>
                      黄游免费下载诞生于一群资深黄游玩家的共同愿景。我们深知玩家在寻找安全可靠的黄游免费下载资源时面临的困扰——
                      病毒风险、虚假链接、过时资源，这些问题一直困扰着广大玩家。
                    </p>
                    <p>
                      因此，我们决定打造一个真正为玩家服务的平台。从最初的手工整理资源列表，到如今涵盖八大分类、
                      超过1200款游戏的综合性黄游免费下载平台，我们始终坚持严格的安全审核和及时的资源更新。
                    </p>
                    <p>
                      我们的口号是&ldquo;黄游免费下载，安全快速，持续更新&rdquo;。这不仅仅是一句口号，
                      更是我们对每一位玩家的郑重承诺。无论你是RPG爱好者、视觉小说迷，还是手机黄游玩家，
                      都能在这里找到心仪的游戏资源。
                    </p>
                  </div>
                </div>
                <div className="rounded-2xl overflow-hidden aspect-[4/3]">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20cozy%20gaming%20workspace%20with%20multiple%20monitors%20displaying%20anime%20game%20art%2C%20warm%20ambient%20lighting%2C%20creative%20studio%20atmosphere%2C%20team%20collaboration%20vibe%2C%20professional%20yet%20casual%20setting&width=800&height=600&seq=about-brand-story&orientation=landscape"
                    alt="品牌故事 - 黄游免费下载"
                    title="黄游免费下载品牌故事"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </section>

            {/* Core Values */}
            <section className="mb-20">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-12">
                核心优势
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { icon: 'ri-shield-check-line', title: '安全可靠', desc: '所有游戏资源经过严格人工审核和多层安全检测，确保不含病毒、木马或恶意软件，让您下载无忧。' },
                  { icon: 'ri-flashlight-line', title: '更新迅速', desc: '每日更新最新黄游资源，新游戏发布后24小时内即可在平台找到，始终走在资源最前沿。' },
                  { icon: 'ri-stack-line', title: '资源丰富', desc: '涵盖角色扮演、模拟经营、视觉小说等八大分类，超过1200款精选游戏，满足各类玩家需求。' },
                ].map((item, index) => (
                  <div key={index} className="p-7 rounded-xl bg-background-50 border border-background-200/70 text-center">
                    <div className="w-14 h-14 mx-auto mb-5 rounded-2xl bg-accent-100 flex items-center justify-center">
                      <i className={`${item.icon} text-accent-600 text-2xl`}></i>
                    </div>
                    <h3 className="font-heading font-bold text-foreground-900 text-lg mb-3">{item.title}</h3>
                    <p className="text-foreground-500 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Team */}
            <section className="mb-20">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-12">
                团队成员
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {teamMembers.map((member, index) => (
                  <div key={index} className="p-6 rounded-xl bg-background-50 border border-background-200/70 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-secondary-100 flex items-center justify-center">
                      <i className={`${member.icon} text-secondary-600 text-2xl`}></i>
                    </div>
                    <h3 className="font-heading font-semibold text-foreground-900 mb-1">{member.name}</h3>
                    <p className="text-xs text-primary-500 font-medium mb-3">{member.role}</p>
                    <p className="text-foreground-500 text-sm leading-relaxed">{member.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Testimonials */}
            <section className="mb-20 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6 py-16">
              <div className="max-w-5xl mx-auto">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-12">
                  玩家口碑
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {testimonials.slice(0, 3).map((item, index) => (
                    <div key={index} className="p-6 rounded-xl bg-background-50 border border-background-200/70">
                      <div className="flex items-center gap-1 mb-3">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <i
                            key={i}
                            className={`text-xs ${i < item.rating ? 'ri-star-fill text-amber-400' : 'ri-star-line text-foreground-300'}`}
                          ></i>
                        ))}
                      </div>
                      <p className="text-foreground-600 text-sm leading-relaxed mb-4">
                        &ldquo;{item.content}&rdquo;
                      </p>
                      <div className="text-sm font-medium text-foreground-800">{item.name}</div>
                      <div className="text-xs text-foreground-400">{item.date}</div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Contact Form */}
            <section className="mb-20" id="contact">
              <div className="max-w-2xl mx-auto">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-4">
                  联系我们
                </h2>
                <p className="text-foreground-500 text-sm text-center mb-10">
                  有任何问题或建议，欢迎通过以下表单与我们联系，我们会在24小时内回复
                </p>

                {formStatus === 'success' ? (
                  <div className="text-center p-10 rounded-2xl bg-accent-50 border border-accent-200">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
                      <i className="ri-check-line text-accent-600 text-2xl"></i>
                    </div>
                    <h3 className="font-heading text-xl font-bold text-foreground-900 mb-2">提交成功！</h3>
                    <p className="text-foreground-500 text-sm">感谢您的留言，我们会尽快回复您。</p>
                    <button
                      onClick={() => setFormStatus('idle')}
                      className="mt-6 px-6 py-2.5 rounded-full bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-all cursor-pointer"
                    >
                      返回表单
                    </button>
                  </div>
                ) : (
                  <form
                    data-readdy-form
                    id="contact-form"
                    onSubmit={handleSubmit}
                    className="space-y-5 p-8 rounded-2xl bg-background-50 border border-background-200/70"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-2">
                          姓名 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          placeholder="请输入您的姓名"
                          className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-2">
                          邮箱 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          placeholder="请输入您的邮箱"
                          className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-2">
                        主题
                      </label>
                      <input
                        id="contact-subject"
                        name="subject"
                        type="text"
                        placeholder="请输入消息主题"
                        className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-2">
                        留言内容 <span className="text-primary-500">*</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={5}
                        placeholder="请输入您的留言内容（最多500字）"
                        className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1 text-right">最多500字</p>
                    </div>

                    {formStatus === 'error' && (
                      <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-600">
                        {formError || '提交失败，请稍后重试'}
                      </div>
                    )}

                    <button
                      type="submit"
                      className="w-full py-3.5 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all cursor-pointer"
                    >
                      <i className="ri-send-plane-line mr-2"></i>
                      发送留言
                    </button>

                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field"
                    />
                  </form>
                )}

                <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {[
                    { icon: 'ri-mail-line', title: '电子邮箱', content: 'support@huangyou-free-download.com' },
                    { icon: 'ri-time-line', title: '工作时间', content: '周一至周日 9:00 - 22:00' },
                    { icon: 'ri-message-2-line', title: '响应时间', content: '24小时内回复' },
                  ].map((item, index) => (
                    <div key={index} className="p-5 rounded-xl bg-background-50 border border-background-200/70 text-center">
                      <div className="w-10 h-10 mx-auto mb-3 rounded-lg bg-secondary-100 flex items-center justify-center">
                        <i className={`${item.icon} text-secondary-600 text-lg`}></i>
                      </div>
                      <div className="text-sm font-medium text-foreground-800 mb-1">{item.title}</div>
                      <div className="text-xs text-foreground-500">{item.content}</div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}